import React, { useState, useEffect } from 'react';
import { ShieldCheckIcon, TrophyIcon, StarIcon, HeartIcon, SparklesIcon, FireIcon } from '@heroicons/react/24/solid';
import { badgesService, type Badge, type BadgeType } from '../../services/badgesService';

interface BadgesDisplayProps {
  userId?: string;
  showAll?: boolean;
  maxDisplay?: number;
  className?: string;
}

// Badge icon and color mapping
const BADGE_CONFIG: Record<BadgeType, { icon: React.ReactNode; color: string; bgColor: string }> = {
  EARLY_SUPPORTER: {
    icon: <SparklesIcon className="w-5 h-5" />,
    color: 'text-purple-600',
    bgColor: 'bg-purple-100',
  },
  YEAR_MEMBER: {
    icon: <TrophyIcon className="w-5 h-5" />,
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-100',
  },
  TOP_PATRON: {
    icon: <StarIcon className="w-5 h-5" />,
    color: 'text-orange-600',
    bgColor: 'bg-orange-100',
  },
  REFERRAL_MASTER: {
    icon: <FireIcon className="w-5 h-5" />,
    color: 'text-red-600',
    bgColor: 'bg-red-100',
  },
  FOUNDING_MEMBER: {
    icon: <ShieldCheckIcon className="w-5 h-5" />,
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
  },
  LOYAL_SUPPORTER: {
    icon: <HeartIcon className="w-5 h-5" />,
    color: 'text-pink-600',
    bgColor: 'bg-pink-100',
  },
};

/**
 * Badges Display Component
 * Shows earned badges on user profiles
 */
const BadgesDisplay: React.FC<BadgesDisplayProps> = ({
  userId,
  showAll = false,
  maxDisplay = 6,
  className = '',
}) => {
  const [badges, setBadges] = useState<Badge[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAllBadges, setShowAllBadges] = useState(showAll);

  useEffect(() => {
    loadBadges();
  }, [userId]);

  const loadBadges = async () => {
    setLoading(true);
    try {
      if (userId) {
        const res = await badgesService.getUserBadges(userId);
        setBadges(res.badges);
      } else {
        const res = await badgesService.getMyBadges();
        setBadges(res.badges);
      }
    } catch (error) {
      console.error('Failed to load badges:', error);
      setBadges([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={`animate-pulse ${className}`}>
        <div className="flex gap-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="w-12 h-12 bg-gray-200 rounded-lg"></div>
          ))}
        </div>
      </div>
    );
  }

  if (badges.length === 0) {
    return null;
  }

  const displayedBadges = showAllBadges ? badges : badges.slice(0, maxDisplay);
  const remainingCount = badges.length - maxDisplay;

  return (
    <div className={className}>
      <div className="flex items-center gap-2 mb-3">
        <TrophyIcon className="w-5 h-5 text-gray-600" />
        <h3 className="font-semibold text-gray-900">
          Badges {badges.length > 0 && <span className="text-gray-500">({badges.length})</span>}
        </h3>
      </div>

      <div className="flex flex-wrap gap-2">
        {displayedBadges.map((badge) => {
          const config = BADGE_CONFIG[badge.badgeType];
          return (
            <div
              key={badge.id}
              className="group relative"
              title={`${badge.title}${badge.description ? ': ' + badge.description : ''}`}
            >
              <div
                className={`
                  ${config.bgColor} ${config.color}
                  p-3 rounded-lg flex items-center justify-center
                  transform transition-all duration-200
                  hover:scale-110 hover:shadow-md
                  cursor-pointer
                `}
              >
                {badge.iconUrl ? (
                  <img src={badge.iconUrl} alt={badge.title} className="w-5 h-5" />
                ) : (
                  config.icon
                )}
              </div>

              {/* Tooltip */}
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block z-10">
                <div className="bg-gray-900 text-white px-3 py-2 rounded-lg text-sm whitespace-nowrap shadow-lg">
                  <div className="font-semibold">{badge.title}</div>
                  {badge.description && (
                    <div className="text-gray-300 text-xs mt-1">{badge.description}</div>
                  )}
                  <div className="text-gray-400 text-xs mt-1">
                    Earned {new Date(badge.earnedAt).toLocaleDateString('en-US', {
                      month: 'short',
                      year: 'numeric',
                    })}
                  </div>
                  {/* Arrow */}
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                    <div className="border-4 border-transparent border-t-gray-900"></div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {!showAllBadges && remainingCount > 0 && (
          <button
            onClick={() => setShowAllBadges(true)}
            className="bg-gray-100 text-gray-600 px-3 py-3 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
          >
            +{remainingCount} more
          </button>
        )}

        {showAllBadges && badges.length > maxDisplay && (
          <button
            onClick={() => setShowAllBadges(false)}
            className="bg-gray-100 text-gray-600 px-3 py-3 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
          >
            Show less
          </button>
        )}
      </div>
    </div>
  );
};

export default BadgesDisplay;
